import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { KemuServiceProduct, PriceInfo } from '@kemu-io/kemu-types';
import { fetchTiersReducer } from './fetchTiersReducer';
import { RootState } from '../../store';

/**
 * Determines the direction of tier change based on tier levels
 * @param currentTierLevel - User's current tier level
 * @param selectedTierLevel - Selected tier level
 * @returns Direction of change or null if no comparison possible
 */
const determineTierChangeDirection = (
	currentTierLevel?: number, 
	selectedTierLevel?: number
): TierChangeDirection => {
	if (currentTierLevel === undefined || selectedTierLevel === undefined) { return null; }
	
	if (selectedTierLevel > currentTierLevel) { return 'upgrade'; }
	if (selectedTierLevel < currentTierLevel) { return 'downgrade'; }
	return 'same';
};

// Types
export type CheckoutStep = 'pricing' | 'payment' | 'success';
export type TierChangeDirection = 'upgrade' | 'downgrade' | 'same' | null;

export type CheckoutState = {
	/** Controls visibility of the checkout modal */
	isVisible: boolean;
	/** Controls visibility of the checkout drawer */
	isDrawerVisible: boolean;
	/** Current step in the checkout flow */
	currentStep: CheckoutStep;
	/** Selected tier (service product) when user initiates checkout */
	selectedTier?: KemuServiceProduct;
	/** Selected price option for the chosen tier */
	selectedPrice?: PriceInfo;
	/** Direction of tier change (upgrade/downgrade) */
	tierChangeDirection: TierChangeDirection;
	/** True when an action (e.g., payment confirmation) is processing */
	isProcessing: boolean;
	/** Optional user-visible error message */
	error?: string;
	/** Loaded tiers to present in pricing screen */
	tiers: KemuServiceProduct[];
	/** Loading state for tier fetching */
	loading: boolean;
};

/** Initial state for the checkout flow */
export const initialState: CheckoutState = {
	isVisible: false,
	isDrawerVisible: false,
	currentStep: 'pricing',
	selectedTier: undefined,
	selectedPrice: undefined,
	tierChangeDirection: null,
	isProcessing: false,
	error: undefined,
	tiers: [],
	loading: false,
};

/**
 * Payload for opening the checkout modal
 *
 * selectedTier - Optional pre-selected tier to highlight in pricing
 * selectedPrice - Optional pre-selected price option for the tier
 */
export type OpenCheckoutPayload = {
	/** Optional pre-selected tier to highlight in pricing */
	selectedTier?: KemuServiceProduct;
	/** Optional pre-selected price option for the tier */
	selectedPrice?: PriceInfo;
};

export const checkoutSlice = createSlice({
	name: 'checkout',
	initialState,
	reducers: {
		/**
		 * Opens the checkout modal
		 */
		openCheckoutModalAction: (state, action: PayloadAction<OpenCheckoutPayload | undefined>) => {
			state.currentStep = 'pricing';
			state.isVisible = true;
			state.selectedTier = action.payload?.selectedTier;
			state.selectedPrice = action.payload?.selectedPrice;
			// Tier change direction will be calculated when selectedTier is set
			state.tierChangeDirection = null;
			state.error = undefined;
		},

		/**
		 * Closes the checkout modal and resets transient state
		 */
		closeCheckoutModalAction: (state) => {
			state.isVisible = false;
			state.isDrawerVisible = false;
			state.selectedTier = undefined;
			state.selectedPrice = undefined;
			state.tierChangeDirection = null;
			state.isProcessing = false;
			state.error = undefined;
			state.currentStep = 'pricing';
		},

		/**
		 * Opens the checkout drawer
		 */
		openCheckoutDrawerAction: (state) => {
			state.isDrawerVisible = true;
		},

		/**
		 * Toggles the processing state
		 */
		toggleIsProcessingAction: (state, action: PayloadAction<boolean>) => {
			state.isProcessing = action.payload;
		},

		/**
		 * Closes the checkout drawer
		 */
		closeCheckoutDrawerAction: (state) => {
			state.isDrawerVisible = false;
		},

		/**
		 * Sets the selected tier and clears price selection
		 */
		setSelectedTierAction: (state, action: PayloadAction<KemuServiceProduct>) => {
			state.selectedTier = action.payload;
			state.selectedPrice = undefined;
			// Tier change direction will be calculated separately using current user tier
		},

		/**
		 * Sets the selected price for the selected tier
		 */
		setSelectedPriceAction: (state, action: PayloadAction<PriceInfo>) => {
			state.selectedPrice = action.payload;
		},

		/**
		 * Updates the tier change direction based on current and selected tier levels
		 */
		updateTierChangeDirectionAction: (state, action: PayloadAction<{ currentTierLevel: number; selectedTierLevel?: number }>) => {
			const { currentTierLevel, selectedTierLevel } = action.payload;
			const targetLevel = selectedTierLevel ?? state.selectedTier?.tierLevel;
			state.tierChangeDirection = determineTierChangeDirection(currentTierLevel, targetLevel);
		},

		/**
		 * Navigates to a given checkout step
		 */
		goToCheckoutStepAction: (state, action: PayloadAction<CheckoutStep>) => {
			state.currentStep = action.payload;
		},
	},

	// Async thunks
	extraReducers: (builder) => {
		// Attach async reducers
		fetchTiersReducer(builder);
	},
});

export const {
	openCheckoutModalAction,
	closeCheckoutModalAction,
	openCheckoutDrawerAction,
	closeCheckoutDrawerAction,
	setSelectedTierAction,
	setSelectedPriceAction,
	updateTierChangeDirectionAction,
	goToCheckoutStepAction,
	toggleIsProcessingAction,
} = checkoutSlice.actions;

// Selectors
export const selectCheckoutState = (state: RootState): CheckoutState => state.checkout;
export const selectCheckoutVisible = (state: RootState): boolean => state.checkout.isVisible;
export const selectCheckoutDrawerVisible = (state: RootState): boolean => state.checkout.isDrawerVisible;
export const selectCheckoutStep = (state: RootState): CheckoutStep => state.checkout.currentStep;
export const selectSelectedTier = (state: RootState): KemuServiceProduct | undefined => state.checkout.selectedTier;
export const selectSelectedPrice = (state: RootState): PriceInfo | undefined => state.checkout.selectedPrice;

export const selectTierChangeDirection = (state: RootState): TierChangeDirection => state.checkout.tierChangeDirection;
export const selectCheckoutIsProcessing = (state: RootState): boolean => state.checkout.isProcessing;
export const selectCheckoutError = (state: RootState): string | undefined => state.checkout.error;
export const selectCheckoutTiers = (state: RootState): KemuServiceProduct[] => state.checkout.tiers;
export const selectCheckoutLoading = (state: RootState): boolean => state.checkout.loading;

export default checkoutSlice.reducer;


